import { rectSortingStrategy } from "@dnd-kit/sortable";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import {
  CropImage,
  DragHandle,
  reorderList,
  SectionCard,
  SortableArea,
  useSortableItem,
  ZoomableImage,
} from "../HomeBlockShared";
import { ACCENT, DESIGN_IMAGE, designImage, HomeBlock } from "../homeBlockData";
import { HeroConfig, HeroSideBanner, HeroSlide } from "../homeBlockConfig";
import classes from "../homeBlock.module.css";
import {
  BannerOption,
  PickerRow,
  rememberBanner,
  searchBanners,
  SearchPicker,
  useBannerInfo,
} from "../RemotePickers";
import {
  Crop,
  EditableList,
  fieldSetter,
  IconPicker,
  ImagePicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const HERO_IMAGE = designImage(DESIGN_IMAGE.hero, 1600);

// Icon Lucide dùng cho dải cam kết
const ICON_OPTIONS = [
  "truck",
  "shield-check",
  "badge-percent",
  "package",
  "store",
  "gift",
  "headset",
];

const SIDE_SLOTS = ["Ô trên", "Ô giữa", "Ô dưới"];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa có ảnh thật, không lưu
const SAMPLE_CROPS: Record<string, Crop> = {
  "slide-1": { x: 247, y: 167, w: 778, h: 393 },
  "side-1": { x: 1035, y: 168, w: 237, h: 137 },
  "side-2": { x: 1035, y: 313, w: 237, h: 115 },
  "side-3": { x: 1035, y: 433, w: 237, h: 124 },
};

const slide = (id: string, name: string): HeroSlide => ({
  id,
  // id banner chưa biết: tự tìm theo tên trong Danh sách banner khi mở trình sửa
  banner: { id: null, name },
  enabled: true,
});

const side = (id: string, link: string, alt: string): HeroSideBanner => ({
  id,
  desktopImage: null,
  mobileImage: null,
  link,
  alt,
});

const initialConfig = (): HeroConfig => ({
  slides: [
    slide("slide-1", "Laptop AI x ASUS: Kiến tạo tương lai"),
    slide("slide-2", "[Banner vị trí Slider trang chủ, số 2]"),
    slide("slide-3", "[Banner vị trí Slider trang chủ, số 3]"),
    slide("slide-4", "[Banner vị trí Slider trang chủ, số 4]"),
    slide("slide-5", "[Banner vị trí Slider trang chủ, số 5]"),
  ],
  sideBanners: [
    side("side-1", "/khuyen-mai", "Giờ vàng giá sốc"),
    side("side-2", "/vga-rtx-50-series", "RTX 50 Series sức mạnh kiến tạo tương lai"),
    side("side-3", "/build-pc", "Build PC theo phong cách của bạn"),
  ],
  usps: [
    ["truck", "Giao nhanh 2H", "Nội thành Hà Nội & HCM"],
    ["shield-check", "Bảo hành chính hãng", "Tất cả sản phẩm"],
    ["badge-percent", "Trả góp 0%", "Thủ tục đơn giản"],
    ["package", "Đổi trả 15 ngày", "Dễ dàng, nhanh chóng"],
    ["store", "22+ Showroom", "Trải nghiệm thực tế"],
    ["gift", "Tích điểm đổi quà", "Ưu đãi độc quyền"],
  ].map(([icon, title, description], i) => ({
    id: `usp-${i + 1}`,
    icon: lucideIcon(icon),
    title,
    description,
  })),
  hotline: { label: "Tổng đài hỗ trợ", number: "1900 1903" },
});

const SLIDE_COLUMNS = "20px 104px minmax(0, 1fr) 140px 44px 92px";

const bannerPickerRow = (b: BannerOption) => (
  <PickerRow image={b.image} title={b.name} sub={`${b.schedule}${b.link ? ` · ${b.link}` : ""}`} />
);

const SlideRow = ({
  slide,
  onChange,
  onRemove,
}: {
  slide: HeroSlide;
  onChange: (patch: Partial<HeroSlide>) => void;
  onRemove: () => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(slide.id);
  const info = useBannerInfo(slide.banner.id);
  const sampleCrop = SAMPLE_CROPS[slide.id];
  const name = info?.name ?? slide.banner.name;
  const linked = slide.banner.id !== null;

  return (
    <div
      ref={setNodeRef}
      className={classes.listRow}
      style={{ ...style, gridTemplateColumns: SLIDE_COLUMNS, background: "#fff" }}
    >
      <DragHandle label={`Kéo để đổi thứ tự ${name}`} handleProps={handleProps} size={16} />
      <ThumbPicker
        label={name}
        src={slide.image}
        fallbackSrc={info?.image}
        fallbackNote="Ảnh của banner"
        imageId={sampleCrop ? DESIGN_IMAGE.hero : undefined}
        crop={sampleCrop}
        onPick={(url) => onChange({ image: url })}
        onReset={() => onChange({ image: null })}
        resetLabel="Dùng ảnh của banner"
        width={104}
        height={52}
      />
      <Box miw={0}>
        <Text fz={14} fw={600} lh={1.3} c={linked ? undefined : "var(--hb-ink-2)"} truncate>
          {name || "Chưa chọn banner"}
        </Text>
        <Text fz={12} lh={1.3} mt={2} className={classes.muted} truncate>
          {linked
            ? info?.link || `Banner #${slide.banner.id}`
            : "Chưa khớp Danh sách banner, bấm Đổi để chọn"}
        </Text>
      </Box>
      <Text fz={13} className={classes.muted}>
        {info?.schedule ?? "—"}
      </Text>
      <Switch
        color={ACCENT}
        aria-label={`Bật ${name}`}
        checked={slide.enabled}
        onChange={(e) => onChange({ enabled: e.currentTarget.checked })}
      />
      <Group gap={2} wrap="nowrap">
        <SearchPicker
          buttonLabel="Đổi"
          searchPlaceholder="Tìm banner theo tên"
          search={searchBanners}
          itemKey={(b) => String(b.id)}
          renderItem={bannerPickerRow}
          onPick={(b) => {
            rememberBanner(b);
            onChange({ banner: { id: b.id, name: b.name } });
          }}
          buttonProps={{ leftSection: undefined, variant: "subtle", size: "compact-xs" }}
        />
        <ActionIcon variant="subtle" color="gray" aria-label={`Bỏ slide ${name}`} onClick={onRemove}>
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </div>
  );
};

const SideBannerCard = ({
  banner,
  slot,
  showSample,
  onHideSample,
  onChange,
}: {
  banner: HeroSideBanner;
  slot: string;
  showSample: boolean;
  onHideSample: () => void;
  onChange: (patch: Partial<HeroSideBanner>) => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(banner.id);
  return (
    <Stack
      ref={setNodeRef}
      gap={10}
      p={12}
      style={{
        ...style,
        border: "1px solid var(--hb-border)",
        borderRadius: 6,
        background: "#fff",
      }}
    >
      <Group justify="space-between">
        <Text fz={12} fw={600} className={classes.muted}>
          {slot}
        </Text>
        <DragHandle label={`Kéo để đổi vị trí ${slot}`} handleProps={handleProps} size={16} />
      </Group>
      <ImagePicker
        label="Ảnh máy tính"
        src={banner.desktopImage}
        imageId={showSample ? DESIGN_IMAGE.hero : undefined}
        crop={SAMPLE_CROPS[banner.id]}
        onPick={(url) => onChange({ desktopImage: url })}
        onClear={() => {
          onHideSample();
          onChange({ desktopImage: null });
        }}
        height={110}
        maxWidth={9999}
      />
      <ImagePicker
        label="Ảnh điện thoại (tuỳ chọn)"
        src={banner.mobileImage}
        onPick={(url) => onChange({ mobileImage: url })}
        onClear={() => onChange({ mobileImage: null })}
        height={70}
        maxWidth={9999}
      />
      <TextInput
        label="Link"
        value={banner.link}
        onChange={(e) => onChange({ link: e.currentTarget.value })}
      />
      <TextInput
        label="Mô tả ảnh"
        value={banner.alt}
        onChange={(e) => onChange({ alt: e.currentTarget.value })}
      />
    </Stack>
  );
};

const EditHero = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, initialConfig);
  const { slides, sideBanners, usps, hotline } = config;
  // Ảnh mẫu của 3 banner phụ: gỡ thì ẩn, không lưu
  const [hiddenSamples, setHiddenSamples] = useState<string[]>([]);
  const matchedRef = useRef(false);

  const setSlides = fieldSetter(setConfig, "slides");
  const setSides = fieldSetter(setConfig, "sideBanners");

  // Slide mẫu chỉ có tên: tìm đúng tên trong Danh sách banner để lấy id
  useEffect(() => {
    if (matchedRef.current) return;
    matchedRef.current = true;
    slides
      .filter((s) => s.banner.id === null && s.banner.name)
      .forEach((s) => {
        searchBanners(s.banner.name)
          .then((found) => {
            const exact = found.find(
              (b) => b.name.trim().toLowerCase() === s.banner.name.trim().toLowerCase()
            );
            if (!exact) return;
            rememberBanner(exact);
            setSlides((list) =>
              list.map((x) => (x.id === s.id ? { ...x, banner: { id: exact.id, name: exact.name } } : x))
            );
          })
          .catch(() => undefined);
      });
  }, [slides, setSlides]);

  const patchSlide = (id: string, patch: Partial<HeroSlide>) =>
    setSlides((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const chosenBanners = new Set(
    slides.flatMap((s) => (s.banner.id === null ? [] : [String(s.banner.id)]))
  );

  return (
    <Flex wrap="wrap" align="flex-start" gap={16}>
      <Stack gap={16} style={{ flex: "999 1 640px", minWidth: 0 }}>
        <SectionCard
          id="hb-hero-slider"
          title="Slider chính"
          bodyGap={0}
          right={
            <SearchPicker
              buttonLabel="Thêm từ Danh sách banner"
              searchPlaceholder="Tìm banner theo tên"
              search={searchBanners}
              itemKey={(b) => String(b.id)}
              renderItem={bannerPickerRow}
              chosenKeys={chosenBanners}
              onPick={(b) => {
                rememberBanner(b);
                setSlides((list) => [
                  ...list,
                  { id: newId(), banner: { id: b.id, name: b.name }, enabled: true },
                ]);
              }}
            />
          }
        >
          <div
            className={`${classes.listRow} ${classes.listRowHeader}`}
            style={{ gridTemplateColumns: SLIDE_COLUMNS }}
          >
            <span />
            <span>Ảnh</span>
            <span>Banner</span>
            <span>Lịch</span>
            <span>Bật</span>
            <span />
          </div>
          <SortableArea
            ids={slides.map((s) => s.id)}
            onReorder={(from, to) => setSlides((list) => reorderList(list, from, to))}
          >
            {slides.map((s) => (
              <SlideRow
                key={s.id}
                slide={s}
                onChange={(patch) => patchSlide(s.id, patch)}
                onRemove={() => setSlides((list) => list.filter((x) => x.id !== s.id))}
              />
            ))}
          </SortableArea>
          <Text fz={12} mt={10} className={classes.muted}>
            Mỗi slide là một banner trong Danh sách banner, lưu theo id: link và lịch hiển thị sửa ở
            đó. Bấm vào ảnh để dùng ảnh riêng cho slide, không chọn thì lấy ảnh của banner. Slider
            tự chuyển sau 5 giây, dừng khi rê chuột.
          </Text>
        </SectionCard>

        <SectionCard
          id="hb-hero-side"
          title="3 banner phụ bên phải"
          right={
            <Text fz={13} className={classes.muted}>
              Kéo thả để đổi thứ tự ô
            </Text>
          }
        >
          <SortableArea
            ids={sideBanners.map((s) => s.id)}
            strategy={rectSortingStrategy}
            onReorder={(from, to) => setSides((list) => reorderList(list, from, to))}
          >
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={12}>
              {sideBanners.map((banner, i) => (
                <SideBannerCard
                  key={banner.id}
                  banner={banner}
                  slot={SIDE_SLOTS[i]}
                  showSample={!!SAMPLE_CROPS[banner.id] && !hiddenSamples.includes(banner.id)}
                  onHideSample={() => setHiddenSamples((list) => [...list, banner.id])}
                  onChange={(patch) =>
                    setSides((list) =>
                      list.map((s) => (s.id === banner.id ? { ...s, ...patch } : s))
                    )
                  }
                />
              ))}
            </SimpleGrid>
          </SortableArea>
        </SectionCard>

        <SectionCard id="hb-hero-usp" title="Dải cam kết">
          <EditableList
            items={usps}
            onChange={fieldSetter(setConfig, "usps")}
            itemLabel={(u) => u.title}
            columns={[
              { label: "Icon", width: "170px" },
              { label: "Tiêu đề", width: "minmax(0, 1fr)" },
              { label: "Mô tả", width: "minmax(0, 1fr)" },
            ]}
            renderCells={(u, update) => (
              <>
                <IconPicker
                  ariaLabel={`Icon cho ${u.title}`}
                  options={ICON_OPTIONS}
                  value={u.icon}
                  onChange={(value) => value && update({ icon: value })}
                  clearable={false}
                />
                <TextInput
                  aria-label="Tiêu đề cam kết"
                  value={u.title}
                  onChange={(e) => update({ title: e.currentTarget.value })}
                />
                <TextInput
                  aria-label={`Mô tả cho ${u.title}`}
                  value={u.description}
                  onChange={(e) => update({ description: e.currentTarget.value })}
                />
              </>
            )}
            newItem={() => ({ id: newId(), icon: lucideIcon("truck"), title: "", description: "" })}
            addLabel="Thêm cam kết"
            max={6}
            minWidth={560}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Nhãn tổng đài"
              value={hotline.label}
              onChange={(e) => {
                const label = e.currentTarget.value;
                setConfig((c) => ({ ...c, hotline: { ...c.hotline, label } }));
              }}
            />
            <TextInput
              label="Số tổng đài"
              type="tel"
              value={hotline.number}
              onChange={(e) => {
                const number = e.currentTarget.value;
                setConfig((c) => ({ ...c, hotline: { ...c.hotline, number } }));
              }}
            />
          </SimpleGrid>
        </SectionCard>
      </Stack>

      <Stack
        component="aside"
        aria-label="Ảnh thiết kế và mục lục"
        gap={16}
        style={{ flex: "1 1 340px", maxWidth: "100%" }}
      >
        <Paper withBorder radius="md">
          <Text
            component="h2"
            fw={700}
            fz={15}
            m={0}
            px={16}
            py={14}
            style={{ borderBottom: "1px solid var(--hb-border)" }}
          >
            Ảnh thiết kế
          </Text>
          <Box p={16} bg="var(--hb-surface-2)">
            <ZoomableImage imageId={block.thumbId} alt={`Ảnh thiết kế ${block.name}`}>
              <CropImage
                src={HERO_IMAGE}
                alt={`Ảnh thiết kế ${block.name}`}
                x={240}
                y={160}
                w={1040}
                h={480}
              />
            </ZoomableImage>
          </Box>
          <Text fz={12} px={16} pt={10} pb={14} className={classes.muted}>
            Ảnh thiết kế gốc để đối chiếu, không đổi theo nội dung đang sửa. Chỉ cắt phần do block này quản lý; menu đầu trang là phần cố định.
          </Text>
        </Paper>
        <Paper withBorder radius="md" p={8} component="nav" aria-label="Các phần của block">
          <button type="button" className={classes.toc} onClick={() => scrollTo("hb-hero-slider")}>
            Slider chính
          </button>
          <button type="button" className={classes.toc} onClick={() => scrollTo("hb-hero-side")}>
            3 banner phụ bên phải
          </button>
          <button type="button" className={classes.toc} onClick={() => scrollTo("hb-hero-usp")}>
            Dải cam kết
          </button>
        </Paper>
      </Stack>
    </Flex>
  );
};

export default EditHero;
